import {
  coreTokens,
} from "./tokens";

import type {
  GameVisualProfile,
  LayoutMode,
} from "./types";

function px(
  value:
    number,
): string {
  return `${value}px`;
}

export function resolveLayoutMode(
  viewportWidth:
    number,
): LayoutMode {
  if (
    !Number.isFinite(
      viewportWidth,
    ) ||
    viewportWidth < 0
  ) {
    throw new Error(
      "Viewport width must be a non-negative finite number.",
    );
  }

  if (
    viewportWidth <
      720
  ) {
    return "compact";
  }

  if (
    viewportWidth <
      1024
  ) {
    return "medium";
  }

  return "expanded";
}

export function createCoreWebCssVariables():
Readonly<Record<string, string>> {
  const color =
    coreTokens.color;

  const spacing =
    coreTokens.spacing;

  const radius =
    coreTokens.radius;

  const sizing =
    coreTokens.sizing;

  const layout =
    coreTokens.layout;

  const motion =
    coreTokens.motion;

  return {
    "--ctn-color-stage-deep":
      color.stageDeep,

    "--ctn-color-stage-base":
      color.stageBase,

    "--ctn-color-elevated":
      color.elevatedDark,

    "--ctn-color-soft":
      color.softDark,

    "--ctn-text-primary":
      color.textPrimary,

    "--ctn-text-secondary":
      color.textSecondary,

    "--ctn-text-tertiary":
      color.textTertiary,

    "--ctn-text-disabled":
      color.textDisabled,

    "--ctn-text-on-accent":
      color.textOnAccent,

    "--ctn-semantic-success":
      color.success,

    "--ctn-semantic-warning":
      color.warning,

    "--ctn-semantic-danger":
      color.danger,

    "--ctn-semantic-info":
      color.info,

    "--ctn-semantic-focus":
      color.focus,

    "--ctn-chrome-dark":
      color.chromeDark,

    "--ctn-chrome-mid":
      color.chromeMid,

    "--ctn-chrome-light":
      color.chromeLight,

    "--ctn-chrome-highlight":
      color.chromeHighlight,

    "--ctn-chrome-specular":
      color.chromeSpecular,

    "--ctn-space-1":
      px(spacing[1]),

    "--ctn-space-2":
      px(spacing[2]),

    "--ctn-space-3":
      px(spacing[3]),

    "--ctn-space-4":
      px(spacing[4]),

    "--ctn-space-5":
      px(spacing[5]),

    "--ctn-space-6":
      px(spacing[6]),

    "--ctn-space-8":
      px(spacing[8]),

    "--ctn-space-10":
      px(spacing[10]),

    "--ctn-space-12":
      px(spacing[12]),

    "--ctn-space-16":
      px(spacing[16]),

    "--ctn-radius-sm":
      px(radius.sm),

    "--ctn-radius-md":
      px(radius.md),

    "--ctn-radius-lg":
      px(radius.lg),

    "--ctn-radius-xl":
      px(radius.xl),

    "--ctn-radius-full":
      px(radius.full),

    "--ctn-touch-minimum":
      px(
        sizing.touchMinimum,
      ),

    "--ctn-button-height-sm":
      px(sizing.buttonSm),

    "--ctn-button-height-md":
      px(sizing.buttonMd),

    "--ctn-button-height-lg":
      px(sizing.buttonLg),

    "--ctn-layout-gutter-compact":
      px(
        layout.gutterCompact,
      ),

    "--ctn-layout-gutter-standard":
      px(
        layout.gutterStandard,
      ),

    "--ctn-layout-gutter-wide":
      px(
        layout.gutterWide,
      ),

    "--ctn-layout-max-content":
      px(
        layout.maxContent,
      ),

    "--ctn-layout-max-prompt":
      px(
        layout.maxPrompt,
      ),

    "--ctn-layout-max-modal":
      px(
        layout.maxModal,
      ),

    "--ctn-motion-fast":
      `${motion.fast}ms`,

    "--ctn-motion-normal":
      `${motion.normal}ms`,

    "--ctn-motion-slow":
      `${motion.slow}ms`,

    "--ctn-font-display":
      'Georgia, "Times New Roman", serif',

    "--ctn-font-interface":
      'Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',

    "--ctn-font-impact":
      '"Arial Narrow", "Roboto Condensed", Impact, sans-serif',
  };
}

export function createGameWebCssVariables(
  profile:
    GameVisualProfile,
): Readonly<Record<string, string>> {
  const result:
    Record<string, string> = {
      "--ctn-game-primary":
        profile.accents.primary,

      "--ctn-game-secondary":
        profile.accents.secondary ??
        profile.accents.primary,
  };

  for (
    const [
      mechanic,
      value,
    ] of Object.entries(
      profile.mechanicTones ??
        {},
    )
  ) {
    result[
      `--ctn-game-${mechanic}`
    ] = value;
  }

  return result;
}
